import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import { useRef, useEffect, useState, useCallback } from "react";

// Data inputs
const dataInputs = [
  "Property Imagery",
  "Income and Credit",
  "Building Permits",
  "Real Estate Transactions",
  "Neighborhood Trends",
  "Climate & Weather Exposure",
  "Zoning & By-laws",
  "Grants & Incentive Programs",
  "Manufacturer Data",
];

// Outputs
const outputs = [
  {
    title: "Need",
    description: "Machine vision detects wear, damage, and age.",
    color: "#ef4444",
  },
  {
    title: "Capacity",
    description: "Assesses financial data and proxies to predict spending power.",
    color: "#10b981",
  },
  {
    title: "Motivation",
    description: "Detects desire by computing a range of behavioural data.",
    color: "#f59e0b",
  },
];

// Veranda Logo for the center node
const VerandaLogo = ({ className = "w-6 h-6" }) => (
  <svg 
    viewBox="0 0 80 76" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <rect x="0" y="0" width="24" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="56" y="0" width="24" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="8" y="16" width="24" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="48" y="16" width="24" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="16" y="32" width="20" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="44" y="32" width="20" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="22" y="48" width="16" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="42" y="48" width="16" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="30" y="64" width="20" height="10" rx="1.5" fill="#3b82f6"/>
  </svg>
);

export const HowItWorksSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [diagramRef, diagramVisible] = useScrollAnimation({ threshold: 0.1 });
  const containerRef = useRef(null);
  const inputDotsRef = useRef([]);
  const outputDotsRef = useRef([]);
  const centerNodeRef = useRef(null);
  const [paths, setPaths] = useState({ leftPaths: [], rightPaths: [] });

  const calculatePaths = useCallback(() => {
    if (!containerRef.current || !centerNodeRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const centerRect = centerNodeRef.current.getBoundingClientRect();
    
    const centerX = centerRect.left + centerRect.width / 2 - containerRect.left;
    const centerY = centerRect.top + centerRect.height / 2 - containerRect.top;
    const nodeRadius = centerRect.width / 2;

    // Calculate left paths from input dots to center
    const leftPaths = inputDotsRef.current.map((dotRef) => {
      if (!dotRef) return '';
      const dotRect = dotRef.getBoundingClientRect();
      const dotCenterX = dotRect.left + dotRect.width / 2 - containerRect.left;
      const dotCenterY = dotRect.top + dotRect.height / 2 - containerRect.top;
      // Start from right edge of the dot (dot center + half width)
      const startX = dotCenterX + dotRect.width / 2;
      const startY = dotCenterY;
      
      const controlX1 = startX + (centerX - startX) * 0.3;
      const controlX2 = centerX - nodeRadius - 20;
      
      return `M ${startX} ${startY} C ${controlX1} ${startY}, ${controlX2} ${centerY}, ${centerX - nodeRadius} ${centerY}`;
    }).filter(p => p);

    // Calculate right paths from center to output dots
    const rightPaths = outputDotsRef.current.map((dotRef) => {
      if (!dotRef) return '';
      const dotRect = dotRef.getBoundingClientRect();
      const dotCenterX = dotRect.left + dotRect.width / 2 - containerRect.left;
      const dotCenterY = dotRect.top + dotRect.height / 2 - containerRect.top;
      // End at left edge of the dot (dot center - half width)
      const endX = dotCenterX - dotRect.width / 2;
      const endY = dotCenterY;
      
      const controlX1 = centerX + nodeRadius + 20;
      const controlX2 = endX - (endX - centerX) * 0.3;
      
      return `M ${centerX + nodeRadius} ${centerY} C ${controlX1} ${centerY}, ${controlX2} ${endY}, ${endX} ${endY}`;
    }).filter(p => p);

    setPaths({ leftPaths, rightPaths });
  }, []);

  useEffect(() => {
    // Calculate paths after a short delay to ensure DOM is ready
    const timer = setTimeout(calculatePaths, 200);
    window.addEventListener('resize', calculatePaths);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', calculatePaths);
    };
  }, [calculatePaths, diagramVisible]);

  return (
    <section
      className="py-16 md:py-24 px-[5%] border-t border-[#1e1e1e]"
      data-testid="how-it-works-section"
    >
      <div className="max-w-content mx-auto">
        <div
          ref={headerRef}
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.7s ease-out, transform 0.7s ease-out",
          }}
        >
          <p className="text-[0.7rem] font-semibold tracking-[3px] uppercase text-[#6b7280] mb-6">
            HOW IT WORKS
          </p>
          <h2 className="text-[clamp(1.5rem,4vw,3rem)] font-medium tracking-[-0.5px] leading-[1.2] text-white mb-4 max-w-3xl">
            AI that sees <span className="text-[#3b82f6]">what sales reps can't.</span>
          </h2>
          <p className="text-[1rem] text-[#9ca3af] mb-10 md:mb-16 max-w-2xl leading-relaxed">
            AI agents assess every property using dozens of public and proprietary data inputs. As your leads convert, the model gets better at predicting future sales. One score tells you who to call first.
          </p>
        </div>

        {/* Flow Diagram - Always Horizontal, Scales Down */}
        <div 
          ref={diagramRef}
          className="relative"
          style={{
            opacity: diagramVisible ? 1 : 0,
            transition: "opacity 0.8s ease-out 0.1s",
          }}
        >
          <div 
            ref={containerRef}
            className="relative w-full"
            style={{ height: 'clamp(280px, 40vw, 400px)' }}
          >
            {/* SVG Connector Lines */}
            <svg 
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ overflow: 'visible' }}
            >
              {/* Left curved dashed lines */}
              {paths.leftPaths.map((path, idx) => (
                <path
                  key={`left-${idx}`}
                  d={path}
                  fill="none"
                  stroke="#333"
                  strokeWidth="1"
                  strokeDasharray="4 3"
                  style={{
                    opacity: diagramVisible ? 0.6 : 0,
                    transition: `opacity 0.5s ease-out ${0.1 + idx * 0.03}s`,
                  }}
                />
              ))}
              
              {/* Right curved dashed lines */}
              {paths.rightPaths.map((path, idx) => (
                <path
                  key={`right-${idx}`}
                  d={path}
                  fill="none"
                  stroke="#333"
                  strokeWidth="1"
                  strokeDasharray="4 3"
                  style={{
                    opacity: diagramVisible ? 0.6 : 0,
                    transition: `opacity 0.5s ease-out ${0.5 + idx * 0.1}s`,
                  }}
                />
              ))}
            </svg>

            {/* Left Column - Data Inputs */}
            <div className="absolute left-0 top-0 bottom-0 w-[25%] flex flex-col justify-between py-1">
              {dataInputs.map((input, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1 md:gap-2 justify-end"
                  style={{
                    opacity: diagramVisible ? 1 : 0,
                    transform: diagramVisible ? "translateX(0)" : "translateX(-20px)",
                    transition: `opacity 0.5s ease-out ${0.1 + idx * 0.04}s, transform 0.5s ease-out ${0.1 + idx * 0.04}s`,
                  }}
                >
                  <span className="text-[#6b7280] text-[14px] md:text-[clamp(0.45rem,1.2vw,0.75rem)] text-right leading-tight">{input}</span>
                  <div 
                    ref={el => inputDotsRef.current[idx] = el}
                    className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-[#3b82f6] flex-shrink-0" 
                  />
                </div>
              ))}
            </div>

            {/* Center - Veranda AI Node */}
            <div 
              ref={centerNodeRef}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-10"
              style={{
                opacity: diagramVisible ? 1 : 0,
                transform: diagramVisible ? "translate(-50%, -50%) scale(1)" : "translate(-50%, -50%) scale(0.8)",
                transition: "opacity 0.6s ease-out 0.4s, transform 0.6s ease-out 0.4s",
              }}
            >
              <div 
                className="bg-[#0a0a0a] border border-[#3b82f6]/40 rounded-xl md:rounded-2xl flex items-center justify-center shadow-[0_0_60px_rgba(59,130,246,0.12)]"
                style={{ 
                  width: 'clamp(50px, 12vw, 112px)', 
                  height: 'clamp(50px, 12vw, 112px)' 
                }}
              >
                <VerandaLogo className="w-[clamp(20px,5vw,32px)] h-[clamp(20px,5vw,32px)]" />
              </div>
              <span className="text-[clamp(0.4rem,1vw,0.7rem)] font-medium tracking-[2px] uppercase text-[#6b7280] mt-2 md:mt-4">
                VERANDA AI
              </span>
            </div>

            {/* Right Column - Outputs */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[25%] flex flex-col justify-center" style={{ gap: 'clamp(50px, 12vw, 100px)' }}>
              {outputs.map((output, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1 md:gap-2"
                  style={{
                    opacity: diagramVisible ? 1 : 0,
                    transform: diagramVisible ? "translateX(0)" : "translateX(20px)",
                    transition: `opacity 0.5s ease-out ${0.5 + idx * 0.1}s, transform 0.5s ease-out ${0.5 + idx * 0.1}s`,
                  }}
                >
                  <div 
                    ref={el => outputDotsRef.current[idx] = el}
                    className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: output.color }}
                  />
                  <div>
                    <span className="text-white text-[16px] md:text-[clamp(0.55rem,1.4vw,0.85rem)] font-medium leading-tight">{output.title}</span>
                    <p className="text-[#6b7280] text-[14px] md:text-[clamp(0.4rem,1vw,0.7rem)] leading-snug mt-0.5 hidden sm:block">
                      {output.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
